import React from 'react';
import {
  getBreadCrumbSchema,
  TAB_GOLD_SILVER,
} from '../../../constants/stocks/componentStaticData';
import { handleTrack } from '../../../utils/tracker';
import { Breadcrumb, BREADCRUMB_TYPE } from '../../Atoms/BreadCrumb';
import { TopCTA } from '../../cms/layout/components';
import { Table } from '../../Molecules/table';
import { SilverChart } from '../common/Chart/SilverChart';
import {
  GRAM_WISE_SILVER_COLUMN,
  LAST_10_DAYS_SILVER_RATE_COLUMN,
} from '../common/GoldTable/SilverColumns';
import { LinkTabs } from '../common/LinkedTabs';
import { SilverRateCard } from '../common/RateCard/SilverRateCard';
import { Sidebar } from '../common/Sidebar';
import { ALL_CITIES, METALS, SILVER_CITY } from '../common/types';
import { capitalized, getGramWiseSilverData } from '../common/utils';
import { SilverContent } from '../silver-country-page/Content';

interface SILVER_CITY_HOMEPAGE_PROPS {
  goldData: SILVER_CITY;
  cityData: ALL_CITIES;
  city: { type: string; name: string };
  graphData: number[][];
  metal: METALS;
  top_cta: string;
}

export const SilverCityHomepage = ({
  goldData,
  cityData,
  city,
  graphData,
  metal,
  top_cta,
}: SILVER_CITY_HOMEPAGE_PROPS): JSX.Element => {
  const gramWiseData = getGramWiseSilverData(goldData.today);
  const capitalizedCityName = capitalized(city.name);

  const BREADCRUMB: Array<BREADCRUMB_TYPE> = [
    { title: 'Home', link: '/' },
    { title: 'Silver Rate Today', link: `/${metal}-rate-india` },
    { title: `Silver rate in ${capitalizedCityName}`, link: null },
  ];

  const captureEvent = (e: React.MouseEvent<Element>) => {
    handleTrack({
      label: 'top_cta',
      obj: e.target,
    });
  };

  return (
    <div>
      <LinkTabs
        buttonData={TAB_GOLD_SILVER}
        activeValue={metal}
        city={city.name}
      />
      <div className="mt-16 sm:mt-4">
        <TopCTA CTA={top_cta || ''} onClickCB={captureEvent} />
      </div>
      <div className="pt-4 wg-container sm:px-4">
        <div className="py-4">
          <Breadcrumb dataSet={BREADCRUMB} />
        </div>

        <div className="flex gap-10">
          <div className="w-1/4 sm:hidden">
            <Sidebar city={city.name} cityData={cityData} metal={metal} />
          </div>

          <div className="w-3/4 sm:w-full">
            <section className="pb-6">
              <SilverRateCard
                data={goldData.today}
                cities={[
                  ...cityData.country,
                  ...cityData.major_city,
                  ...cityData.metro_city,
                ]}
                selectedCity={city.name}
              />
            </section>

            <section className="my-12">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                Silver Price Chart and Trend in {capitalizedCityName}
              </h2>

              <div className="py-6">
                <SilverChart
                  data={graphData ? graphData : []}
                  city={city.name}
                />
              </div>
            </section>

            <section className="my-12">
              <h2 className="text-s-28 font-bold text-font-500 sm:text-s-20">
                Silver Price Per gram/kilogram in {capitalizedCityName} Today
              </h2>

              <div className="py-6">
                <Table
                  columns={GRAM_WISE_SILVER_COLUMN}
                  tableId={'gramWiseTable'}
                  data={gramWiseData}
                  viewMoreToggle={false}
                />
              </div>
            </section>

            <section className="my-12">
              <h2 className="text-s-28 font-bold sm:text-base">
                Silver Rate in {capitalizedCityName} for Last 10 Days
              </h2>

              <div className="py-6">
                <Table
                  columns={LAST_10_DAYS_SILVER_RATE_COLUMN}
                  tableId={'lastTenDaysRates'}
                  data={goldData.last_ten_days_data}
                  viewMoreToggle={false}
                />
              </div>
            </section>

            <section className="my-12">
              <SilverContent />
            </section>
          </div>
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getBreadCrumbSchema(BREADCRUMB)),
          }}
        ></script>
      </div>
    </div>
  );
};
